import { z } from 'zod';
import { ORDER_STATUS, ORDER_TYPE, CANCELED_BY, ORDER_LIMITS, PAYMENT_METHOD } from '../constants/index.ts';

// Order item option schema
export const orderItemOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  price: z.number().min(0),
});

// Order item schema for creating orders
// Note: MAX_QUANTITY_PER_ITEM is enforced server-side based on restaurant config
export const createOrderItemSchema = z.object({
  menuItemId: z.string(),
  quantity: z.number().int().min(1).max(ORDER_LIMITS.MAX_QUANTITY_PER_ITEM),
  selectedOptions: z.array(z.string()).optional(), // Option IDs
});

// Full order item schema
export const orderItemSchema = z.object({
  id: z.string(),
  menuItemId: z.string(),
  name: z.string(),
  quantity: z.number().int().min(1).max(ORDER_LIMITS.MAX_QUANTITY_PER_ITEM),
  unitPrice: z.number().min(0),
  options: z.array(orderItemOptionSchema),
});

// Order summary schema
export const orderSummarySchema = z.object({
  itemsCount: z.number().int().min(0),
  subtotal: z.number().min(0),
  tax: z.number().min(0).optional(),
  taxRate: z.number().min(0).max(100).optional(),
  taxName: z.string().optional(),
  deliveryFee: z.number().int().min(0).optional(),
  total: z.number().min(0),
});

// Create order input schema
// Note: Additional validations (min/max amount, rate limiting, etc.) are enforced server-side
export const createOrderSchema = z.object({
  restaurantId: z.string(),
  customerTelegramId: z.string(),
  customerName: z.string().min(1).max(100),
  customerFirstName: z.string().min(1).max(50).optional(),
  customerLastName: z.string().max(50).optional(),
  customerUsername: z.string().max(50).optional(),
  orderType: z.enum([ORDER_TYPE.PICKUP, ORDER_TYPE.DELIVERY]),
  tableNumber: z.string().max(20).optional(),
  items: z.array(createOrderItemSchema).min(1).max(ORDER_LIMITS.MAX_ITEMS_PER_ORDER),
  notes: z.string().max(500).optional(),
  paymentMethod: z.enum([PAYMENT_METHOD.CASH, PAYMENT_METHOD.CARD]).default(PAYMENT_METHOD.CASH),
  // Scheduled orders: ISO datetime string for when the order should be ready (e.g., "2026-03-26T18:00:00")
  scheduledFor: z.string().datetime({ offset: true }).optional(),
  // Delivery location fields (required when orderType is DELIVERY)
  deliveryLatitude: z.number().min(-90).max(90).optional(),
  deliveryLongitude: z.number().min(-180).max(180).optional(),
  deliveryAddress: z.string().max(500).optional(),
}).superRefine((data, ctx) => {
  if (data.orderType === ORDER_TYPE.DELIVERY) {
    if (data.deliveryLatitude === undefined || data.deliveryLatitude === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'La ubicación es requerida para pedidos de delivery',
        path: ['deliveryLatitude'],
      });
    }
    if (data.deliveryLongitude === undefined || data.deliveryLongitude === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'La ubicación es requerida para pedidos de delivery',
        path: ['deliveryLongitude'],
      });
    }
  }
});

// Update order status schema
export const updateOrderStatusSchema = z.object({
  status: z.enum([
    ORDER_STATUS.SCHEDULED,
    ORDER_STATUS.PENDING,
    ORDER_STATUS.PREPARING,
    ORDER_STATUS.READY,
    ORDER_STATUS.SHIPPED,
    ORDER_STATUS.DELIVERED,
    ORDER_STATUS.CANCELED,
  ]),
  canceledBy: z
    .enum([CANCELED_BY.CUSTOMER, CANCELED_BY.RESTAURANT])
    .optional(),
});

// Types inferred from schemas
export type CreateOrderInput = z.infer<typeof createOrderSchema>;
export type CreateOrderItemInput = z.infer<typeof createOrderItemSchema>;
export type UpdateOrderStatusInput = z.infer<typeof updateOrderStatusSchema>;
export type OrderItemInput = z.infer<typeof orderItemSchema>;
export type OrderSummaryInput = z.infer<typeof orderSummarySchema>;

